/* ============================================================================
   FaithCore OS — effects.js
   Ambient atmosphere generator. Loaded with `defer`.

   Design notes:
   - Nothing runs until the browser is idle, so effects never compete with LCP.
   - All DOM is built in one DocumentFragment per host → one reflow, not N.
   - Effects self-disable on: prefers-reduced-motion, Save-Data, low core
     count, and when their host scrolls out of view (IntersectionObserver
     pauses the animations so offscreen sections cost nothing).
   - Config arrives from Liquid as data-* attributes on [data-fc-effects].
   ========================================================================= */
(() => {
  'use strict';

  const motionOK = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const saveData = navigator.connection && navigator.connection.saveData;
  // Very low-end devices get atmosphere-free (but fully functional) pages.
  const lowPower = (navigator.hardwareConcurrency || 8) <= 2;
  const enabled = motionOK && !saveData && !lowPower;

  const rand = (min, max) => Math.random() * (max - min) + min;
  const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 200));

  /* -- Feather artwork ------------------------------------------------------
     One inline path, reused. Drawn as a stylised primary feather so it reads
     at 18–34px without detail noise. */
  const FEATHER = `<svg viewBox="0 0 24 48" aria-hidden="true" focusable="false"><path d="M12 0c5 7 8 14 8 22 0 9-4 17-8 26-4-9-8-17-8-26C4 14 7 7 12 0zm0 8c-2.6 4.4-4 9.2-4 14 0 6.4 2 12.6 4 18V8z" opacity=".9"/></svg>`;

  /* ------------------------------------------------------------------------
     Builders. Each returns the number of nodes appended.
     ---------------------------------------------------------------------- */
  function buildFeathers(layer, count) {
    const frag = document.createDocumentFragment();
    for (let i = 0; i < count; i++) {
      const el = document.createElement('span');
      el.className = 'fc-feather';
      el.innerHTML = FEATHER;
      el.style.setProperty('--x', `${rand(-5, 100)}%`);
      el.style.setProperty('--size', `${rand(14, 32).toFixed(1)}px`);
      el.style.setProperty('--dur', `${rand(20, 42).toFixed(1)}s`);
      // Negative delay: feathers are already mid-fall on first paint, so the
      // section never starts visibly "empty".
      el.style.setProperty('--delay', `${-rand(0, 40).toFixed(1)}s`);
      el.style.setProperty('--drift', `${rand(-140, 140).toFixed(0)}px`);
      el.style.setProperty('--peak', rand(0.18, 0.55).toFixed(2));
      frag.appendChild(el);
    }
    layer.appendChild(frag);
    return count;
  }

  function buildDust(layer, count) {
    const frag = document.createDocumentFragment();
    for (let i = 0; i < count; i++) {
      const el = document.createElement('span');
      el.className = 'fc-dust';
      el.style.setProperty('--x', `${rand(0, 100)}%`);
      el.style.setProperty('--y', `${rand(35, 105)}%`);
      el.style.setProperty('--size', `${rand(2, 5).toFixed(1)}px`);
      el.style.setProperty('--dur', `${rand(9, 22).toFixed(1)}s`);
      el.style.setProperty('--delay', `${-rand(0, 20).toFixed(1)}s`);
      el.style.setProperty('--drift', `${rand(-60, 60).toFixed(0)}px`);
      el.style.setProperty('--peak', rand(0.3, 0.9).toFixed(2));
      frag.appendChild(el);
    }
    layer.appendChild(frag);
    return count;
  }

  function buildRays(layer) {
    const wrap = document.createElement('span');
    wrap.className = 'fc-rays';
    // Fixed at three blades. More reads as fog, fewer reads as an accident.
    const specs = [
      { x: '12%', w: 220, from: -12, to: -2, dur: 22, purple: true },
      { x: '46%', w: 150, from: 4, to: 12, dur: 18, purple: false },
      { x: '78%', w: 190, from: -6, to: 5, dur: 26, purple: false }
    ];
    specs.forEach((s, i) => {
      const ray = document.createElement('span');
      ray.className = `fc-ray${s.purple ? ' fc-ray--purple' : ''}`;
      ray.style.setProperty('--x', s.x);
      ray.style.setProperty('--w', `${s.w}px`);
      ray.style.setProperty('--from', `${s.from}deg`);
      ray.style.setProperty('--to', `${s.to}deg`);
      ray.style.setProperty('--dur', `${s.dur}s`);
      ray.style.setProperty('--delay', `${-i * 3}s`);
      wrap.appendChild(ray);
    });
    layer.appendChild(wrap);
    return specs.length;
  }

  /* ------------------------------------------------------------------------
     Pause anything that scrolls offscreen. This is the single biggest win:
     an idle homepage with five effect hosts only ever animates the one in
     view.
     ---------------------------------------------------------------------- */
  const visibility = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          entry.target.style.animationPlayState = entry.isIntersecting ? 'running' : 'paused';
          entry.target.querySelectorAll('.fc-feather, .fc-dust, .fc-ray').forEach((n) => {
            n.style.animationPlayState = entry.isIntersecting ? 'running' : 'paused';
          });
        });
      }, { rootMargin: '120px' })
    : null;

  /* ------------------------------------------------------------------------
     Boot each [data-fc-effects] host.
     Attributes (all written by snippets/effects-layer.liquid):
       data-feathers="12"  data-dust="26"  data-rays="true"
     ---------------------------------------------------------------------- */
  function init(root = document) {
    if (!enabled) return;

    root.querySelectorAll('[data-fc-effects]').forEach((host) => {
      if (host.dataset.fcReady === 'true') return;
      host.dataset.fcReady = 'true';

      const layer = document.createElement('div');
      layer.className = 'fc-fx';
      layer.setAttribute('aria-hidden', 'true');

      // Halve the particle budget on phones — smaller viewport, same density.
      const scale = window.innerWidth < 750 ? 0.5 : 1;
      const feathers = Math.round((parseInt(host.dataset.feathers, 10) || 0) * scale);
      const dust = Math.round((parseInt(host.dataset.dust, 10) || 0) * scale);

      let nodes = 0;
      if (host.dataset.rays === 'true') nodes += buildRays(layer);
      if (feathers > 0) nodes += buildFeathers(layer, feathers);
      if (dust > 0) nodes += buildDust(layer, dust);

      if (nodes === 0) return;
      host.classList.add('fc-fx-host');
      host.prepend(layer);
      if (visibility) visibility.observe(layer);
    });
  }

  /* ------------------------------------------------------------------------
     Parallax. One listener for the whole page, rAF-throttled, writes a CSS
     variable instead of touching layout properties.
     ---------------------------------------------------------------------- */
  function initParallax() {
    const targets = Array.from(document.querySelectorAll('[data-fc-parallax]'));
    if (!targets.length || !motionOK) return;

    let ticking = false;
    const update = () => {
      ticking = false;
      for (const el of targets) {
        const host = el.closest('[data-fc-parallax-host]') || el.parentElement;
        const rect = host.getBoundingClientRect();
        // Skip offscreen hosts entirely.
        if (rect.bottom < -200 || rect.top > window.innerHeight + 200) continue;
        const speed = parseFloat(el.dataset.fcParallax) || 0.25;
        // Progress: -1 (below fold) → 1 (above fold)
        const progress = (rect.top + rect.height / 2 - window.innerHeight / 2) / window.innerHeight;
        el.style.setProperty('--fc-parallax', `${(progress * speed * 100).toFixed(2)}px`);
      }
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /* ------------------------------------------------------------------------
     Reading progress bar (article template).
     ---------------------------------------------------------------------- */
  function initProgress() {
    const bar = document.querySelector('[data-fc-progress]');
    const target = document.querySelector('[data-fc-progress-target]');
    if (!bar || !target) return;
    let ticking = false;
    const update = () => {
      ticking = false;
      const rect = target.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      const read = total > 0 ? Math.min(Math.max(-rect.top / total, 0), 1) : 0;
      bar.style.setProperty('--fc-read', read.toFixed(4));
    };
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }, { passive: true });
    update();
  }

  idle(() => { init(); initParallax(); initProgress(); });

  // Theme Editor: sections are re-rendered without a page load.
  document.addEventListener('shopify:section:load', (e) => {
    init(e.target);
    initParallax();
  });

  window.FaithCoreEffects = { init, enabled };
})();
